import "@/lib/zod-config";
import { enrichGeoPageview } from "@/lib/analytics/geo-pageview";
import { deferJourneyPageview } from "@/lib/analytics/journey-pageview";
import posthog from "posthog-js";
import { redactBrowserLog } from "@/lib/telemetry/browser-log-redaction";
import {
  isUnreleasedBuild,
  resolveTelemetryEnvironment,
} from "@/lib/telemetry-runtime";
import { hasAccessibleStorage } from "@/lib/storage/safe-storage";
import { registerProvider } from "@/lib/analytics/tracker";
import { posthogProvider } from "@/lib/analytics/providers/posthog";
import { gtmProvider } from "@/lib/analytics/providers/gtm";
import { errorBreadcrumbProvider } from "@/lib/analytics/providers/error-breadcrumb";
import {
  posthogManagedProxyHost,
  posthogProjectToken,
  posthogUiHost,
} from "../../../analytics-transport";
import { filterPosthogNoiseExceptions } from "@/lib/error-tracking/posthog-exception-filter";
import { groupConvexServerErrors } from "@/lib/error-tracking/convex-server-error-fingerprint";
import {
  isBearerTokenVisit,
  scheduleReplayStart,
} from "@/lib/analytics/replay-gate";
import { redactBearerEvent } from "@shared/analytics/redact-bearer-credentials";
import { redactPersistedLandingUrls } from "@/lib/analytics/redact-persisted-landing-urls";
import { currentTrafficClass } from "@/lib/analytics/traffic-class";
import { TRAFFIC_CLASS_PROPERTY } from "@shared/analytics/traffic-class";
import { snapshotDocumentRelease } from "@/lib/retired-deployment";
import { installPreloadErrorRecovery } from "@/lib/error-tracking/preload-error-recovery";
import { installNavigationSignalRecoverableErrorTracking } from "@/lib/error-tracking/navigation-signal-recoverable-error";
import { isEmbedDocument } from "@/lib/embed/is-embed-document";
import {
  WEB_VITALS_EVENT,
  startWebVitalsReporting,
} from "@/lib/analytics/web-vitals";
import {
  NAVIGATION_TIMING_EVENT,
  installNavigationTimer,
} from "@/lib/performance/navigation-timing";

snapshotDocumentRelease();

if (typeof window !== "undefined") {
  installPreloadErrorRecovery(window);
  installNavigationSignalRecoverableErrorTracking(window);
  window.dataLayer = window.dataLayer ?? [];
}

const isFramedEmbed =
  typeof window !== "undefined" && isEmbedDocument(window.location.pathname);

const projectToken = posthogProjectToken();

if (projectToken && !isFramedEmbed) {
  const posthogRelease = process.env.NEXT_PUBLIC_RELEASE_SHA || undefined;
  posthog.init(projectToken, {
    api_host: posthogManagedProxyHost(),
    ui_host: posthogUiHost(),
    defaults: "2026-01-30",
    person_profiles: "identified_only",
    capture_pageleave: true,
    capture_exceptions: !isUnreleasedBuild(),
    logs: {
      captureConsoleLogs: true,
      environment: resolveTelemetryEnvironment(),
      serviceVersion: posthogRelease,
      beforeSend: (record) => redactBrowserLog(record, window.location.href),
    },
    loaded: () => redactPersistedLandingUrls(posthog),
    before_send: (event) =>
      redactBearerEvent(
        enrichGeoPageview(
          deferJourneyPageview(groupConvexServerErrors(filterPosthogNoiseExceptions(event))),
        ),
      ),
    disable_session_recording: true,
    session_recording: {
      maskAllInputs: true,
      recordBody: false,
      recordHeaders: false,
      captureCanvas: { recordCanvas: false },
    },
    persistence: hasAccessibleStorage("localStorage")
      ? "localStorage+cookie"
      : "cookie",
    disable_conversations: true,
    capture_performance: { web_vitals: false },
  });

  posthog.register({
    environment: resolveTelemetryEnvironment(),
    [TRAFFIC_CLASS_PROPERTY]: currentTrafficClass(),
    ...(posthogRelease ? { $git_commit_sha: posthogRelease } : {}),
  });

  registerProvider(posthogProvider);
  void startWebVitalsReporting((properties) => {
    posthog.capture(WEB_VITALS_EVENT, properties, { transport: "sendBeacon" });
  });
  if (!isBearerTokenVisit()) {
    scheduleReplayStart(() => posthog.startSessionRecording());
  }
}

registerProvider(gtmProvider);
registerProvider(errorBreadcrumbProvider);

if (typeof window !== "undefined" && !isFramedEmbed) {
  installNavigationTimer((properties) => {
    if (projectToken) posthog.capture(NAVIGATION_TIMING_EVENT, properties);
  });
}
